import './index.scss';

import React from 'react';

import ContextMenu from '@components/ContextMenu';
import Song from '@data/interfaces/song.interface';

import appleMusicIcon from 'url:@assets/images/services/apple-music.png';
import spotifyIcon from 'url:@assets/images/services/spotify.png';
import youtubeMusicIcon from 'url:@assets/images/services/youtube-music.jpg';
import tidalIcon from 'url:@assets/images/services/tidal.jpg';

export function TrashCanIcon() {
  return <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.1582 13.3594C2.20898 13.3594 1.61719 12.8086 1.57617 11.8652L1.18359 3.48047H0.580078C0.257812 3.48047 0 3.23438 0 2.90625C0 2.58398 0.257812 2.33789 0.580078 2.33789H3.12891V1.47656C3.12891 0.544922 3.71484 0 4.73438 0H6.9668C7.98633 0 8.57812 0.544922 8.57812 1.47656V2.33789H11.1328C11.4609 2.33789 11.7129 2.58398 11.7129 2.90625C11.7129 3.23438 11.4609 3.48047 11.1328 3.48047H10.5293L10.1367 11.8594C10.0957 12.8086 9.49805 13.3594 8.55469 13.3594H3.1582ZM4.32422 1.52344V2.33789H7.38281V1.52344C7.38281 1.24219 7.18945 1.06641 6.87891 1.06641H4.82227C4.51758 1.06641 4.32422 1.24219 4.32422 1.52344ZM4.04883 11.5137C4.33008 11.5137 4.51172 11.3379 4.5 11.0742L4.31836 4.76367C4.3125 4.49414 4.125 4.33008 3.86133 4.33008C3.58008 4.33008 3.4043 4.5 3.41602 4.76953L3.5918 11.0801C3.60352 11.3438 3.78516 11.5137 4.04883 11.5137ZM5.85938 11.5137C6.12891 11.5137 6.31641 11.3438 6.31641 11.0801V4.76367C6.31641 4.5 6.12891 4.33008 5.85938 4.33008C5.58398 4.33008 5.39648 4.5 5.39648 4.76367V11.0801C5.39648 11.3438 5.58398 11.5137 5.85938 11.5137ZM7.66406 11.5137C7.92773 11.5137 8.10938 11.3438 8.12109 11.0801L8.29688 4.76953C8.30859 4.5 8.13281 4.33008 7.85156 4.33008C7.58789 4.33008 7.40039 4.5 7.39453 4.76367L7.21289 11.0742C7.20117 11.3379 7.38281 11.5137 7.66406 11.5137Z" fill="#F64D4D" />
  </svg>;
}

export type SongContextMenuProps = {
  song: Song;
  menuRef: React.RefObject<HTMLDivElement>;
  x: number;
  y: number;
  isClosing: boolean;
  onRequestClose?: () => void;
  onRemove?: (song: Song) => void;
};

type PlatformStatus = 'matched' | 'linked' | 'unavailable';

export default function SongContextMenu(props: SongContextMenuProps) {
  const platformRows = buildPlatformRows(props.song);

  return <ContextMenu
    className='song-context-menu'
    x={props.x}
    y={props.y}
    isClosing={props.isClosing}
    onRequestClose={props.onRequestClose}
    sections={[
      {
        className: 'song-context-menu-links',
        items: platformRows.map((row, index) => ({
          key: `${row.platform}-${index}`,
          className: `song-context-menu-link plain ${row.url ? '' : 'disabled'}`,
          href: row.url || undefined,
          disabled: !row.url,
          content: <>
            <p className={`platform-icon platform-${toPlatformSlug(row.platform)}`}>
              {renderPlatformIcon(row.platform)}
            </p>
            <div className='platform-copy'>
              <span className='platform-name'>{row.platform}</span>
              <span className='platform-status'>
                {row.status === 'matched' ? 'Matched' : null}
                {row.status === 'linked' ? 'Linked to Search' : null}
                {row.status === 'unavailable' ? 'Unavailable' : null}
              </span>
            </div>
          </>
        }))
      },
      {
        className: 'song-context-menu-actions',
        items: [{
          key: 'remove',
          className: 'song-context-action remove',
          onClick: () => {
            props.onRemove?.(props.song);
          },
          content: <>
            <TrashCanIcon />
            <span>Remove</span>
          </>
        }]
      }
    ]}
  />;
}

function buildPlatformRows(song: Song): Array<{
  platform: Song['attributes']['streamingLinks'][number]['platform'];
  status: PlatformStatus;
  url: string | null;
}> {
  const platforms: Song['attributes']['streamingLinks'][number]['platform'][] = [
    'Apple Music',
    'Spotify',
    'YouTube Music',
    'Tidal'
  ];

  return platforms.map((platform) => {
    const directLink = song.attributes.streamingLinks.find((link) => link.platform === platform);
    const externalID = song.attributes.externalIDs.find((id) => id.platform === platform);

    let status: PlatformStatus = 'unavailable';

    if (externalID) {
      status = 'matched';
    } else if (directLink) {
      status = 'linked';
    }

    return {
      platform,
      status,
      url: directLink?.url || null
    };
  });
}

function toPlatformSlug(platform: Song['attributes']['streamingLinks'][number]['platform']) {
  return platform.toLowerCase().replace(/\s+/g, '-');
}

function renderPlatformIcon(platform: Song['attributes']['streamingLinks'][number]['platform']) {
  if (platform === 'YouTube Music') {
    return <img src={youtubeMusicIcon} alt="" aria-hidden="true" />;
  }

  if (platform === 'Apple Music') {
    return <img src={appleMusicIcon} alt="" aria-hidden="true" />;
  }

  if (platform === 'Spotify') {
    return <img src={spotifyIcon} alt="" aria-hidden="true" />;
  }

  return <img src={tidalIcon} alt="" aria-hidden="true" />;
}
